import { useEffect, useState, useMemo } from 'react';
import { DialogContent, Grid, DialogTitle, CircularProgress, Typography } from '@mui/material';
import { RoundedDialog, User, UserGroup, StandardDialogActions } from '../../../Global/Components';
import StateManager from '../../../Global/StateManager';
import { FormatDate } from '../../../Global/Functions';
import { useHistory } from 'react-router-dom';
import { isEmpty, last, first, isArray } from 'lodash';
import UserInput from '../../../Global/DataGrid/Filters/UserInput';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import moment from 'moment';
import axios from 'axios';
import { FormIcon, ProcessIcon, TaskIcon } from '../../../Global/Icons';

const TYPES_ICONS = {
  task: <TaskIcon />,
  form: <FormIcon />,
  process: <ProcessIcon />,
};

export default function EntriesDialog({ open, onClose, entriesIds, title }) {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!isArray(entriesIds)) return;
    const params = entriesIds.reduce(
      (prev, curr) => {
        const splitted = String(curr).split(':');
        const type = first(splitted);
        const id = last(splitted);
        if (type === 'task') {
          prev.tasks.push(id);
        }
        if (type === 'form') {
          prev.forms.push(id);
        }
        if (type === 'process') {
          prev.processes.push(id);
        }
        return prev;
      },
      { tasks: [], forms: [], processes: [] },
    );
    setLoading(true);
    axios
      .post('/fields/kpi/resolveEntries', params)
      .then(({ data }) => {
        setRows([
          ...data.tasks.map((x) => ({ ...x, type: 'task', completedAt: x.completedDate })),
          ...data.formEntries.map((x) => ({ ...x, type: 'form' })),
          ...data.processEntries.map((x) => ({ ...x, type: 'process' })),
        ]);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [entriesIds]);

  const columns = useMemo(() => {
    const usersOnlyOperators = [
      {
        label: 'Includes',
        value: 'includes',
        InputComponent: UserInput,
        InputComponentProps: { type: 'user' },
        getValueAsString: (value) => (isArray(value) ? value.join(', ') : ''),
      },
    ];

    const result = [
      {
        width: 250,
        headerName: 'Source',
        field: 'title',
        display: 'flex',
        renderCell: ({ row }) => (
          <Grid container alignItems="center" wrap="nowrap">
            {TYPES_ICONS[row.type]}
            <Typography noWrap sx={{ ml: 2 }}>
              {row.title}
            </Typography>
          </Grid>
        ),
      },
      {
        width: 80,
        headerName: '#',
        field: 'number',
        display: 'flex',
        valueGetter: (value, row) => (row.type === 'task' ? null : value),
      },
      {
        width: 220,
        headerName: 'Assigned users',
        field: 'assignedUsers',
        sortable: false,
        display: 'flex',
        renderCell: ({ row }) => (row.assignedUsers ? <UserGroup avatarSize={32} ids={row.assignedUsers} /> : '-'),
        valueGetter: (value) => '',
        filterOperators: usersOnlyOperators,
      },
      {
        width: 300,
        headerName: 'Sent',
        field: 'createdAt',
        type: 'date',
        display: 'flex',
        valueGetter: (value) => (value ? moment(value).toDate() : null),
        renderCell: ({ row }) => (row.createdAt ? FormatDate(row.createdAt) : '-'),
      },
      {
        width: 300,
        headerName: 'Due',
        field: 'dueAt',
        type: 'date',
        display: 'flex',
        valueGetter: (value) => (value ? moment(value).toDate() : null),
        renderCell: ({ row }) => (row.dueAt ? FormatDate(row.dueAt) : '-'),
      },
      {
        width: 300,
        headerName: 'Completed',
        field: 'completedAt',
        type: 'date',
        display: 'flex',
        valueGetter: (value) => (value ? moment(value).toDate() : null),
        renderCell: ({ row }) => (row.completedAt ? FormatDate(row.completedAt) : '-'),
      },
    ];

    return result;
  }, []);

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>{title || 'Entries'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Grid container item alignItems="center" justifyContent="center" style={{ height: '70vh' }}>
            <CircularProgress color="primary" />
          </Grid>
        ) : (
          <Grid container item style={{ height: '70vh' }} alignContent="flex-start">
            <DataGridPremium
              disableAggregation
              disableRowGrouping
              getRowId={(row) => row._id}
              columns={columns}
              rows={rows}
              disableRowSelectionOnClick
              onRowClick={({ row }) => {
                if (row.type === 'task') {
                  StateManager.selectTask(row._id);
                }
                if (row.type === 'form') {
                  history.push(`/forms/entry/${row._id}`);
                }
                if (row.type === 'process') {
                  history.push(`/processes/ongoing/${row._id}`);
                }
              }}
            />
          </Grid>
        )}
      </DialogContent>

      <StandardDialogActions onClose={onClose} />
    </RoundedDialog>
  );
}
